// store/auth.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useUserLocal } from '../hooks/users/useUserLocal';
import { useCallback } from 'react';
import { env } from '@/shared/config';
import {
  clearTokens,
  getRefreshToken,
  onSessionExpired,
  setTokens,
} from '@/shared/api/session';
// Импорт из листового модуля, а не из бочки entities/auth: бочка тянет хуки,
// которые сами импортируют этот стор, и получается цикл.
import type { NativeRole, NativeUser } from '@/entities/auth/model/types';
import { hasElevatedRole } from '@/entities/auth/model/types';

/**
 * Стор держит сразу две личности, потому что бэк на переходный период тоже
 * двойной:
 *
 *  - `userId` / `role` — старая сессия Bitrix24, числовой id. На нём висят все
 *    legacy-экраны (аналитика, таймсит, чаты отделов и проектов, отделы).
 *  - `nativeUser` / `roles` — нативная сессия v2, UUID. На неё смотрят
 *    `/v2/tasks`, `/v2/groups`, `/v2/users`.
 *
 * Моста между ними нет и не будет: бэк отказался связывать нативные аккаунты
 * с Bitrix (P1-1 в ответе от 23.08.2026). Поэтому под нативной сессией
 * `userId` остаётся `null`, а legacy-экраны просто недоступны.
 */
interface AuthState {
  /** Bitrix-id старой сессии. Под нативной сессией — всегда `null`. */
  userId: number | null;
  role: string | null;
  isAdmin: boolean;
  isAuthenticated: boolean;
  sessionExpiresAt: number | null;

  /** Пользователь нативной сессии. `null`, если вошли через Bitrix24. */
  nativeUser: NativeUser | null;
  roles: NativeRole[];

  setAuth: (
    userId: number | null,
    role: string | null,
    sessionExpiresAt?: number | null
  ) => void;
  /** Применяет результат `/auth/register`, `/auth/login` или `/auth/me`. */
  setNativeSession: (
    user: NativeUser,
    tokens?: { access_token: string; refresh_token: string }
  ) => void;
  logout: () => void;
  isSessionValid: () => boolean;
}

const getBypassAuthState = () => ({
  userId: env.authBypassUserId,
  role: env.authBypassRole,
  isAdmin: env.authBypassRole === 'admin',
  isAuthenticated: true,
  sessionExpiresAt: null,
  nativeUser: null,
  roles: [] as NativeRole[],
});

const getInitialAuthState = () =>
  env.authBypass
    ? getBypassAuthState()
    : {
        userId: null,
        role: null,
        isAdmin: false,
        isAuthenticated: false,
        sessionExpiresAt: null,
        nativeUser: null,
        roles: [] as NativeRole[],
      };

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...getInitialAuthState(),

      setAuth: (
        userId: number | null,
        role: string | null,
        sessionExpiresAt = null
      ) => {
        const isAdmin = role === 'admin';
        const expiresAt = sessionExpiresAt || Date.now() + 2 * 60 * 60 * 1000;

        set({
          userId,
          role,
          isAdmin,
          isAuthenticated: true,
          sessionExpiresAt: expiresAt,
        });
      },

      setNativeSession: (user, tokens) => {
        if (tokens) {
          setTokens(tokens.access_token, tokens.refresh_token);
        }

        const roles = user.roles ?? [];

        set({
          nativeUser: user,
          roles,
          role: roles[0] ?? null,
          isAdmin: hasElevatedRole(roles),
          isAuthenticated: true,
          // Срок жизни нативной сессии определяет refresh-токен на бэке, а не
          // мы: access живёт 15 минут и молча обновляется перехватчиком.
          sessionExpiresAt: null,
        });
      },

      logout: () => {
        clearTokens();
        set(getInitialAuthState());
      },

      isSessionValid: () => {
        if (env.authBypass) {
          return true;
        }

        const state = get();

        // Нативная сессия: валидность определяет наличие пользователя, а
        // протухший access молча меняется на новый по refresh-токену.
        if (state.nativeUser) {
          return true;
        }

        return state.sessionExpiresAt
          ? state.sessionExpiresAt > Date.now()
          : false;
      },
    }),
    {
      name: 'auth-storage',
      // v2 — появилась нативная сессия. У всех, кто логинился раньше, в
      // localStorage лежит `isAuthenticated: true` с числовым userId и без
      // нативных токенов: такое состояние проходит гварды, но каждый запрос
      // отдаёт 401. Поэтому всё, что старше второй версии, сбрасываем.
      version: 2,
      migrate: () => getInitialAuthState() as AuthState,
      partialize: (state) => ({
        userId: state.userId,
        role: state.role,
        isAdmin: state.isAdmin,
        isAuthenticated: state.isAuthenticated,
        sessionExpiresAt: state.sessionExpiresAt,
        nativeUser: state.nativeUser,
        roles: state.roles,
      }),
      merge: (persistedState, currentState) => {
        if (env.authBypass) {
          return {
            ...currentState,
            ...getBypassAuthState(),
          };
        }

        const persisted = (persistedState ?? {}) as Partial<AuthState>;

        // Нативная сессия жива только вместе со своими токенами. Если
        // localStorage почистили частично (или разлогинились в другой
        // вкладке), восстанавливать пользователя нельзя.
        if (persisted.nativeUser && !getRefreshToken()) {
          return { ...currentState, ...getInitialAuthState() };
        }

        return {
          ...currentState,
          ...persisted,
        };
      },
    }
  )
);

/**
 * Сетевой слой не импортирует стор напрямую (иначе цикл), поэтому логаут по
 * истечении сессии он вызывает через эту подписку.
 */
onSessionExpired(() => {
  useAuthStore.getState().logout();
});

/**
 * Вошли по нативной учётной записи, а не через Bitrix24. От этого зависит,
 * какие legacy-подсистемы вообще имеет смысл запускать: без Bitrix-личности
 * сокеты `/ws`, `/ws/intermediate` и таймсит для нас закрыты.
 */
export const useIsNativeSession = () =>
  useAuthStore((state) => Boolean(state.nativeUser));

export const useAuthWithUserData = () => {
  const {
    userId,
    role,
    sessionExpiresAt,
    nativeUser,
    roles,
    setAuth: setStoreAuth,
    logout,
    isAuthenticated: storeIsAuthenticated,
  } = useAuthStore();

  const { isLoading: usersLoading, getDisplayNameById } =
    useUserLocal.useUsersMap();

  // Под нативной сессией имя приходит вместе с сессией и справочник Bitrix
  // для этого не нужен.
  const fullName = nativeUser
    ? nativeUser.displayName
    : userId
      ? getDisplayNameById(userId)
      : null;

  const isAdmin = nativeUser ? hasElevatedRole(roles) : role === 'admin';
  const isAuthenticated =
    env.authBypass ||
    (storeIsAuthenticated &&
      (nativeUser
        ? true
        : sessionExpiresAt
          ? sessionExpiresAt > Date.now()
          : false));

  const setAuthWithUserData = useCallback(
    (userId: number, role: string, sessionExpiresAt?: number | null) => {
      setStoreAuth(userId, role, sessionExpiresAt);
    },
    [setStoreAuth]
  );

  return {
    fullName,
    userId,
    /** UUID нативного пользователя. Именно он уходит в `/v2/*`. */
    nativeUserId: nativeUser?.id ?? null,
    nativeUser,
    roles,
    role,
    isAdmin,
    isAuthenticated,
    setAuth: setAuthWithUserData,
    logout,
    isLoading: nativeUser ? false : usersLoading,
  };
};

export const usePermissions = () => {
  const { isAdmin } = useAuthStore();

  return {
    canEditDescription: true,
    canEditTitle: isAdmin,
    canEditStatus: isAdmin,
    canEditPriority: isAdmin,
    canEditAssignee: isAdmin,
    canEditDeadline: isAdmin,
    canDeleteTasks: isAdmin,
    canAddComments: true,
    canViewAdminPanel: isAdmin,
    canManageUsers: isAdmin,
  };
};
